import { nonEmptyString } from 'src/modules/shared/utils/formatting/non-empty-string.util';
import React from 'react';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { Text } from 'src/modules/shared/components/atoms/text';
import Loading from 'src/modules/shared/components/atoms/loading';
import { WorkflowCanvas } from '../molecules/workflow-canvas/workflow-canvas';
import { WorkflowDetailsPanel } from '../molecules/workflow-details-panel';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { WORKFLOW_START_NODE_ID } from '../molecules/workflow-canvas/workflow-canvas';
import { useUnsavedNavigationGuardState } from 'src/modules/shared/hooks/unsaved-navigation-guard';
import { UnsavedChangesModal } from 'src/modules/shared/components/molecules/modals/unsaved-changes-modal';
import { useActions } from 'src/modules/shared/hooks/api/automations/actions';
import { WorkflowActionPickerModal } from 'src/modules/tools/components/molecules/workflow-editor/workflow-action-picker-modal';
import { WorkflowEditorHeaderActions } from 'src/modules/tools/components/molecules/workflow-editor/workflow-editor-header-actions';
import { WorkflowFormErrorBanner } from 'src/modules/tools/components/molecules/workflow-editor/workflow-form-error-banner';
import { useWorkflowEdit } from 'src/modules/tools/hooks/workflow-editor/use-workflow-edit';
import { useWorkflowEditorDraft } from 'src/modules/tools/hooks/workflow-editor/use-workflow-editor-draft';
import { useWorkflowEditorUnsavedState } from 'src/modules/tools/hooks/workflow-editor/use-workflow-editor-unsaved';
import type { WorkflowEditPageProps } from 'src/modules/tools/types/workflow-edit.types';
import type { WorkflowEditorMetadataDraft } from 'src/modules/tools/types/workflow-editor.types';
import type { WorkflowDefinitionDraft } from 'src/modules/tools/utils/workflow-definition.type';

const WORKFLOW_END_NODE_ID = '__end__';
const DEFAULT_INPUT_SCHEMA: Record<string, unknown> = {
  type: 'object',
  properties: {},
};

/**
 * Edit view for workflow versions. Currently renders the canvas/details with
 * placeholder actions for saving once mutations are wired.
 */
export const WorkflowEditPage: React.FC<WorkflowEditPageProps> = ({
  workflowId,
}) => {
  const [definitionDraft, setDefinitionDraft] = React.useState<
    WorkflowDefinitionDraft | undefined
  >(undefined);

  const [inputSchemaDraft, setInputSchemaDraft] = React.useState<
    Record<string, unknown>
  >(() => DEFAULT_INPUT_SCHEMA);

  const [selectedStepId, setSelectedStepId] = React.useState<
    string | undefined
  >();
  const [inputValidationErrors, setInputValidationErrors] = React.useState<
    string[]
  >([]);
  const { isBlocked, proceed, stay, setHasUnsavedChanges, hasUnsavedChanges } =
    useUnsavedNavigationGuardState(false);
  const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);
  const [actionSearch, setActionSearch] = React.useState('');
  const [metadataDraft, setMetadataDraft] =
    React.useState<WorkflowEditorMetadataDraft>({});
  const markSavedRef = React.useRef<() => void>(() => undefined);
  const handleSaveSuccess = React.useCallback(() => {
    markSavedRef.current();
  }, []);
  const { actions: catalogActions, loading: loadingActions } = useActions(
    undefined,
    isAddModalOpen,
    true
  );
  const {
    pendingFinal,
    filteredActions,
    reachableSteps,
    hasSteps,
    hasInitialStep,
    handleRequestAddStep,
    handleRequestAddFinal,
    handleSelectAction,
    closeActionPicker,
    handleAddConnection,
    handleRemoveConnection,
    handleChangeStepInputs,
    handleDeleteStep,
  } = useWorkflowEditorDraft({
    definitionDraft,
    setDefinitionDraft,
    setSelectedStepId,
    actions: catalogActions,
    actionSearch,
    setActionSearch,
    setIsAddModalOpen,
    startNodeId: WORKFLOW_START_NODE_ID,
    endNodeId: WORKFLOW_END_NODE_ID,
  });

  React.useEffect(() => {
    const schema = definitionDraft?.inputSchema;
    setInputSchemaDraft(schema ?? DEFAULT_INPUT_SCHEMA);
  }, [definitionDraft]);

  const {
    workflowVersion,
    workflowMeta,
    formattedUpdatedAt,
    loading,
    error,
    formError,
    handleBack,
    handleSave,
    isSaving,
  } = useWorkflowEdit({
    workflowId,
    definitionDraft,
    inputSchemaDraft,
    metadataDraft,
    inputValidationErrors,
    hasUnsavedChanges,
    defaultInputSchema: DEFAULT_INPUT_SCHEMA,
    onSaveSuccess: handleSaveSuccess,
    setDefinitionDraft,
    setInputSchemaDraft,
    setMetadataDraft,
  });

  const versionKey = React.useMemo(
    () =>
      workflowVersion
        ? `${workflowVersion.workflowId}-${workflowVersion.updatedAt}`
        : null,
    [workflowVersion]
  );

  const { markSaved } = useWorkflowEditorUnsavedState({
    definitionDraft,
    inputSchemaDraft,
    metadataDraft,
    setHasUnsavedChanges,
    baselineKey: versionKey,
  });

  React.useEffect(() => {
    markSavedRef.current = markSaved;
  }, [markSaved]);

  const canSave =
    !!definitionDraft &&
    hasSteps &&
    hasInitialStep &&
    inputValidationErrors.length === 0 &&
    !isSaving;

  return (
    <div className="flex flex-col h-full">
      <UnsavedChangesModal
        isOpen={isBlocked}
        onProceed={proceed}
        onStay={stay}
        isSaving={isSaving}
      />
      <Panel className="h-full flex flex-col">
        <PanelHeader
          fixed
          className="px-4 py-3 border-b"
          actions={
            <WorkflowEditorHeaderActions
              onBack={handleBack}
              onSave={handleSave}
              canSave={canSave}
              isSaving={isSaving}
            />
          }
        >
          <div className="flex flex-wrap items-center gap-2">
            <Text
              as="h2"
              size="h2"
              weight="medium"
              className="text-slate-900 break-words cursor-pointer"
              onClick={() => setSelectedStepId(undefined)}
            >
              {nonEmptyString(
                nonEmptyString(metadataDraft.title) ?? workflowMeta?.name
              ) ?? workflowId}
            </Text>
            <Text size="small" className="text-slate-500">
              Editando borrador
            </Text>
          </div>
        </PanelHeader>
        <div className="flex-1 overflow-auto p-4">
          <div className="mb-3">
            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
              <span>ID: {workflowId}</span>
              {formattedUpdatedAt && (
                <span>Actualizado: {formattedUpdatedAt}</span>
              )}
            </div>
            <WorkflowFormErrorBanner message={formError} />
          </div>
          {loading ? (
            <div className="h-[70vh] flex items-center justify-center">
              <Loading size="lg" />
            </div>
          ) : error ? (
            <div className="h-[70vh] flex items-center justify-center">
              <ErrorState
                title="Error cargando workflow"
                description={error.message}
                size="sm"
              />
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1 min-w-0">
                <WorkflowCanvas
                  definition={definitionDraft}
                  selectedStepId={selectedStepId}
                  onSelectStep={setSelectedStepId}
                  editable
                  onAddConnection={handleAddConnection}
                  onRemoveConnection={handleRemoveConnection}
                  onAddStep={handleRequestAddStep}
                  onDeleteStep={handleDeleteStep}
                  onAddFinalStep={
                    definitionDraft?.finalStep
                      ? undefined
                      : handleRequestAddFinal
                  }
                  canAddFinalStep={!definitionDraft?.finalStep}
                  reachableStepIds={reachableSteps}
                />
              </div>
              <WorkflowDetailsPanel
                definition={definitionDraft}
                selectedStepId={selectedStepId}
                editable
                inputSchema={inputSchemaDraft}
                onChangeInputSchema={setInputSchemaDraft}
                onChangeStepInputs={handleChangeStepInputs}
                onValidationChange={setInputValidationErrors}
                metadata={metadataDraft}
                onChangeMetadata={setMetadataDraft}
                onDeleteStep={handleDeleteStep}
              />
            </div>
          )}
        </div>
      </Panel>
      <WorkflowActionPickerModal
        isOpen={isAddModalOpen}
        onClose={closeActionPicker}
        pendingFinal={pendingFinal}
        actionSearch={actionSearch}
        onActionSearchChange={setActionSearch}
        actions={filteredActions}
        loading={loadingActions}
        onSelectAction={handleSelectAction}
      />
    </div>
  );
};

export default WorkflowEditPage;
